import PropTypes from "prop-types"
import React, { Component } from "react"
import Relay from "react-relay"
import { Link } from "react-router"
import ResultMedia from "../elements/ResultMedia"
import Icon from "../elements/Icon"
import {
  resourceFileFragment,
  getImageData,
  getPlaceholder
} from "../../scripts/resource"
import api from "../../api"
import State from "../../stores/State"
import { injectIntl, FormattedMessage } from "react-intl"
import messages from "../../messages/sets"
import settingsMessages from "../../messages/settings"
import Hexagon from "../elements/Hexagon"
import { getAccountSettings } from "../../scripts/accountSettings"
import { getStyles, getStyleTag } from "../../styles/dynamicStyles"

class SetResult extends Component {
  static propTypes = {
    userSet: PropTypes.object.isRequired,
    view: PropTypes.string,
    withEdit: PropTypes.bool
  }

  static defaultProps = {
    view: "favourites",
    withEdit: true
  }

  state = {
    isEditing: false
  }

  constructor(props) {
    super(props)
    this.state = {
      name:
        props.userSet.name == "Favourites"
          ? props.intl.formatMessage(messages.favourites)
          : props.userSet.name,
      isPublic: props.userSet.isPublic
    }
  }

  getImages = () => {
    const userSet = this.props.userSet
    const resources = userSet.resources.edges.map(edge => edge.node)
    return resources.reduce((images, resource) => {
      const image = getImageData(resource)
      if (image) {
        images.push(image)
      }
      return images
    }, [])
  }

  removeSet = (id, e) => {
    if (e) e.preventDefault()

    if (window.confirm(this.props.intl.formatMessage(messages.confirmDelete))) {
      api("DELETE", `user_sets/${id}`).then(() => {
        this.props.relay.forceFetch()
      })
    }
  }

  renameSet = ({ target: { value: name } }) => {
    this.setState({ name })
  }

  updateSet = () => {
    const {
      relay,
      userSet: { sqlId }
    } = this.props
    const { name, isPublic } = this.state
    api("PATCH", `user_sets/${sqlId}`, {
      name,
      public: isPublic
    }).then(() => relay.forceFetch())
  }

  onPublic = () => {
    this.setState(
      {
        isPublic: !this.state.isPublic
      },
      this.updateSet
    )
  }

  onEdit = () => {
    const { isEditing } = this.state
    if (isEditing) {
      this.updateSet()
    } else {
      setTimeout(() => {
        if (this.nameInput) {
          this.nameInput.focus()
          //the following put the cursor at the end of the input
          const v = this.nameInput.value
          this.nameInput.value = ""
          this.nameInput.value = v
        }
      }, 300)
    }
    this.setState({ isEditing: !isEditing })
  }

  render() {
    const {
      withEdit,
      linkTo,
      userSet: { id, sqlId, sub },
      intl
    } = this.props
    const { name, isPublic, isEditing } = this.state
    const isMine = State.get("userId") === sub
    const link = linkTo || `/user/sets/${id}`
    const accountSettings = getAccountSettings(
      intl.formatMessage(settingsMessages.authenticAccountId)
    )
    const {
      style: { content }
    } = accountSettings.settings

    return (
      <article
        className="c-article"
        data-article-type="set"
        itemScope
        itemType="http://schema.org/Article"
      >
        {getStyleTag([
          "user-sets__hexagon",
          "data-article-type-flag",
          "data-article-type-icon"
        ])}
        <div style={getStyles("c-article-inner")}>
          <ResultMedia
            linkTo={link}
            title={name}
            resultType="set"
            images={this.getImages()}
            placeholder={getPlaceholder(
              name,
              intl.formatMessage(settingsMessages.authenticAccountId)
            )}
          />
          <div
            className="c-article__content-wrap"
            style={getStyles("c-article__content-wrap")}
          >
            <div
              className="c-article__content"
              style={getStyles([
                "c-article__content",
                "c-article__content__with-hexagon"
              ])}
            >
              {content === "type1" && (
                <Hexagon
                  icon="heart"
                  color="#fff"
                  style={getStyles(["c-article__content__hexagon"])}
                />
              )}
              <h6
                className="c-article__subtitle"
                style={getStyles("c-article__subtitle")}
              >
                {isPublic && <FormattedMessage {...messages.publicSet} />}
                {!isPublic && <FormattedMessage {...messages.privateSet} />}
              </h6>
              <div className="c-article__title-item-actions-wrap">
                <h4
                  className="c-article__title"
                  itemProp="name"
                  style={getStyles([
                    "c-article__title",
                    "user-sets-c-article__title"
                  ])}
                >
                  {!isEditing && <Link to={link}>{name}</Link>}
                  {isEditing && (
                    <input
                      ref={input => {
                        this.nameInput = input
                      }}
                      className="input--onwhite"
                      type="text"
                      value={name}
                      onChange={this.renameSet}
                    />
                  )}
                </h4>
                {withEdit &&
                  isMine && (
                    <div className="c-article__item-actions">
                      <button
                        onClick={this.onEdit}
                        className="c-article__item-action"
                        title={intl.formatMessage(messages.editSetName)}
                      >
                        <Icon
                          icon={this.state.isEditing ? "tick" : "edit"}
                          style={getStyles("user-sets__icon")}
                        />
                      </button>
                      <button
                        onClick={this.onPublic}
                        className={`c-article__item-action ${
                          isPublic ? "" : "state-inactive"
                        }`}
                        title={intl.formatMessage(
                          isPublic
                            ? messages.makeSetPrivate
                            : messages.makeSetPublic
                        )}
                      >
                        <Icon icon="eye" style={getStyles("user-sets__icon")} />
                      </button>
                      <button
                        onClick={e => this.removeSet(sqlId, e)}
                        className="c-article__item-action link--alert"
                        title={intl.formatMessage(messages.deleteThisSet)}
                      >
                        <Icon
                          icon="trash"
                          style={getStyles("user-sets__icon")}
                        />
                      </button>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </article>
    )
  }
}

export default Relay.createContainer(injectIntl(SetResult), {
  fragments: {
    userSet: () => Relay.QL`
      fragment on UserSet {
        id
        sqlId: sql_id
        name
        isPublic: public
        sub
        resources(first: 6) {
          edges {
            node {
              id
              ${resourceFileFragment}
            }
          }
        }
      }
    `
  }
})



// WEBPACK FOOTER //
// ./webapp/components/modules/SetResult.js